import { z } from 'zod';
import {
  EVENT_SOURCES,
  type EventSource,
  INBOX_STATUSES,
  type InboxStatus,
  MODULES,
  type ModuleKey,
  SEVERITIES,
  type Severity,
} from '../enums';
import { addDaysLocal, type LocalDate, toLocalDate } from '../time';
import { AUTOMATION_EVENT_TYPES } from './automations';
import { idSchema, recordLinkSchema } from './common';

/**
 * Zdarzenia i Inbox (MVP §9, §14; ARCHITECTURE §9; Figma 02 Inbox): `events`, `inbox_items`
 * (packages/db/src/schema/events.ts). Moduł uprawnień: `inbox`.
 *
 * Event opisuje, co się wydarzyło (źródło, czas, powiązania, ważność, powód klasyfikacji); Inbox to lista zdarzeń
 * wymagających uwagi danej osoby (`inbox_items`: status uwagi, odłożenie, zrobione). Nie każde zdarzenie generuje push
 * (tylko `critical`, opcjonalnie `approval`, patrz contracts/notifications.ts).
 *
 * Ważność: `critical` (Pilne) — potwierdzona awaria albo wiarygodne pilne zgłoszenie; `approval` (Akceptacja) — czeka
 * na decyzję; `normal` (Zwykłe) — zwykłe zapytanie, feedback; `fyi` (Do wiadomości) — do historii (np. „Dzięki”).
 * Właściciel może poprawić klasyfikację: `severity_override` + `override_by/at`; obowiązuje `effective_severity`
 * (kolumna generowana `coalesce(severity_override, severity)`), oryginał zostaje w `severity`.
 * Wejścia i wyjścia usług dopisuje agent modułu events/inbox.
 */

export { EVENT_SOURCES, type EventSource, INBOX_STATUSES, type InboxStatus, SEVERITIES, type Severity };
export const severitySchema = z.enum(SEVERITIES);
export const eventSourceSchema = z.enum(EVENT_SOURCES);
export const inboxStatusSchema = z.enum(INBOX_STATUSES);

/** Kolejność ważności (sortowanie: Pilne najpierw). */
export const SEVERITY_RANK: Readonly<Record<Severity, number>> = { critical: 0, approval: 1, normal: 2, fyi: 3 };

export function compareSeverity(a: Severity, b: Severity): number {
  return SEVERITY_RANK[a] - SEVERITY_RANK[b];
}

/** Ważność obowiązująca (po poprawce właściciela). */
export function effectiveSeverity(e: {
  severity: Severity | string;
  severityOverride?: Severity | string | null;
}): Severity {
  return (e.severityOverride ?? e.severity) as Severity;
}

/**
 * Stan elementu Inbox przy utworzeniu: `fyi` trafia do Inboxu jako przeczytany (historia, bez kropki i plakietki
 * — Figma 02: „Anna · REA — Dzięki, czekam na FAQ” bez wyróżnienia), pozostałe jako nieprzeczytane.
 */
export const INBOX_INITIAL_STATUS: Readonly<Record<Severity, InboxStatus>> = {
  critical: 'unread',
  approval: 'unread',
  normal: 'unread',
  fyi: 'read',
};

/**
 * Ważności, które liczą się do plakietki „Inbox” (nieprzeczytane wymagające uwagi). Bez `approval`: akcje do
 * akceptacji liczy plakietka „Akceptacje”, a w Inboxie jest tylko baner z liczbą (kompozycja Fali 4).
 */
export const INBOX_BADGE_SEVERITIES = ['critical', 'normal'] as const satisfies readonly Severity[];

// ── Grupy listy (Figma 02: „Pilne”, „Nowe”, „Wcześniej”) ──────────────────────

export const INBOX_GROUPS = ['urgent', 'new', 'earlier'] as const;
export type InboxGroup = (typeof INBOX_GROUPS)[number];

/** Ile dni wstecz element jest „Nowy” (dziś i wczoraj, daty Europe/Warsaw). */
export const INBOX_NEW_DAYS = 2;

/**
 * Grupa elementu: niezrobione `critical` → „Pilne”; zdarzenie z ostatnich `INBOX_NEW_DAYS` dni albo nieprzeczytane
 * → „Nowe”; reszta → „Wcześniej”.
 */
export function inboxGroupOf(
  item: { status: InboxStatus; severity: Severity; occurredAt: Date },
  today: LocalDate,
): InboxGroup {
  if (item.severity === 'critical' && item.status !== 'done') return 'urgent';
  const since = addDaysLocal(today, -(INBOX_NEW_DAYS - 1));
  if (item.status === 'unread' || toLocalDate(item.occurredAt) >= since) return 'new';
  return 'earlier';
}

/** Element odłożony (`snoozed_until` w przyszłości) nie jest pokazywany w „Otwarte” ani liczony w plakietce. */
export function isInboxSnoozed(item: { snoozedUntil: Date | null }, now: Date): boolean {
  return item.snoozedUntil !== null && item.snoozedUntil.getTime() > now.getTime();
}

// ── Typy zdarzeń używane przez kilka modułów ─────────────────────────────────

/** Znane typy zdarzeń (moduły mogą dodawać własne `<moduł>.<co>`). */
export const EVENT_TYPES = {
  approvalRequested: 'approval.requested',
  approvalExecuted: 'approval.executed',
  approvalFailed: 'approval.failed',
  approvalExpired: 'approval.expired',
  renewalDue: 'renewal.due',
  monitorDown: 'monitor.down',
  monitorUp: 'monitor.up',
  messageReceived: 'message.received',
  /** wysłany mail (po akceptacji `email.send`) — historia klienta (Fala 4) */
  messageSent: 'message.sent',
  /** integracja utraciła autoryzację / błąd (Fala 4, `INTEGRATION_EVENT_TYPES`) */
  integrationAuthLost: 'integration.auth_lost',
  integrationError: 'integration.error',
  /** faktura odrzucona w KSeF (Fala 4, `billing.payments.sync`) — „Zwykłe”, raz na status */
  invoiceKsefError: 'billing.invoice.ksef_error',
  agentRunFinished: 'agent_run.finished',
  vaultRotationDue: 'vault.rotation_due',
  /** spotkanie z Fravity OS usunięte w kalendarzu osoby → archiwum (DECISIONS 2026-10-10 [S], `CALENDAR_EVENT_TYPES`) */
  meetingCalendarRemoved: 'meeting.calendar_removed',
  /** zadanie przypisane osobie przez kogoś innego (17.10.2026, push „Zadania przypisane do mnie i komentarze”) */
  taskAssigned: 'task.assigned',
  /** komentarz do zadania osoby (wykonawca, autor) — 17.10.2026 */
  taskCommented: 'task.commented',
  /** nowe zgłoszenie z portalu klienta → opiekun klienta, bez opiekuna owner i admini (17.10.2026) */
  clientRequestCreated: 'client_request.created',
  /** wiadomość klienta w rozmowie zgłoszenia (17.10.2026) */
  clientRequestMessage: 'client_request.message',
} as const;
export type KnownEventType = (typeof EVENT_TYPES)[keyof typeof EVENT_TYPES];

/**
 * Zdarzenia z treścią od osoby klienta z portalu (tytuł zgłoszenia, opis, wiadomość): dla AI i MCP to dane niezaufane
 * jak mail — oznaczenie `from_client` / blok danych (łata 17.10.2026, DECISIONS „Zgłoszenie klienta w zleceniach dla
 * Claude”).
 */
export const CLIENT_REQUEST_EVENT_TYPES: readonly string[] = [
  EVENT_TYPES.clientRequestCreated,
  EVENT_TYPES.clientRequestMessage,
];

export function isClientRequestEventType(type: string): boolean {
  return CLIENT_REQUEST_EVENT_TYPES.includes(type);
}

/**
 * Zdarzenia z danymi sejfu (etykiety dostępów, nazwy zasobów): widoczne tylko dla osób z sejfem każdego zasobu
 * z linków (filtr `eventVisibleWhere` w @fos/core), także w historii klienta i w Inboxie zespołu.
 */
export const VAULT_EVENT_TYPES = [EVENT_TYPES.vaultRotationDue] as const;

/**
 * Zdarzenia osobiste (Przegląd Fali 10): „Powiadom mnie” z automatyzacji — pozycja w Inboxie autora reguły, często
 * z podsumowaniem AI także jego prywatnej poczty. Widzi je tylko odbiorca (ma pozycję Inboxu), nie zespół: bez osi czasu
 * klienta (także MCP `get_client_history`), Inboxu zespołu i alarmów zespołu w Dziś (filtr `eventVisibleWhere`).
 */
export const PERSONAL_EVENT_TYPES: readonly string[] = [
  AUTOMATION_EVENT_TYPES.notify,
  AUTOMATION_EVENT_TYPES.notifyPush,
  // spotkanie usunięte w kalendarzu osoby (DECISIONS 2026-10-10 [S]) — informacja dla właściciela kalendarza
  EVENT_TYPES.meetingCalendarRemoved,
];

/** Nadawca do wyświetlenia (Figma 02: „Monitoring”, „gabinet.lumi@gmail.com”, „Tomasz · Szkółka”), max 120 znaków. */
export const eventSenderSchema = z.string().trim().min(1).max(120);

/** Klocek pól zdarzenia (zapis przez `ctx.emit`; tu do walidacji danych z integracji i seeda). */
export const eventFieldsSchema = z.object({
  type: z.string().min(1).max(80),
  severity: severitySchema,
  title: z.string().trim().min(1).max(300),
  body: z.string().max(20_000).nullable().optional(),
  source: eventSourceSchema,
  sender: eventSenderSchema.nullable().optional(),
  clientId: idSchema.nullable().optional(),
  projectId: idSchema.nullable().optional(),
  module: z.enum(MODULES).nullable().optional(),
  links: z.array(recordLinkSchema).max(20).default([]),
  /** powód klasyfikacji („Monitoring: 2 nieudane sprawdzenia z rzędu”, „AI: prośba o wycenę”) */
  reason: z.string().trim().max(500).nullable().optional(),
  dedupKey: z.string().min(1).max(200).nullable().optional(),
});
export type EventFields = z.infer<typeof eventFieldsSchema>;

// ── Inbox a historia (MVP §9: „FYI — informacja do historii”) ───────────────

/**
 * Widoki Inboxu nie pokazują zdarzeń o obowiązującej ważności `fyi`: to historia (karta klienta → Historia,
 * `inbox.clientTimeline`), nie „uwaga”. Element Inboxu FYI istnieje (status `read`, `INBOX_INITIAL_STATUS`) jako
 * zapis odbiorców — po poprawce klasyfikacji na wyższą ważność wraca do Inboxu jako nieprzeczytany.
 * Od kompozycji Fali 4 także `approval` („Akcja czeka na akceptację”; DECISIONS, rozstrzygnięcia pytań z Fali 3):
 * Inbox pokazuje akcje tylko banerem z liczbą i linkiem do Akceptacji, bez osobnych pozycji. Element Inboxu zostaje
 * (odbiorcy, zamykanie po decyzji, push według ustawień). Zdarzenia o wyniku akcji („Nie udało się wykonać”) mają
 * inną ważność i są na liście.
 * Wyjątek: jawny filtr ważności (`severity: 'fyi'` / `'approval'`) — tak właściciel znajduje źle sklasyfikowane.
 * Wyjątek od 17.10.2026 (DECISIONS [P] „Każda poczta w Inboxie, push tylko o istotnych”): odebrany mail „Do
 * wiadomości” (`INBOX_VISIBLE_FYI_TYPES`) JEST w Inboxie jako „Mniej ważne” (od razu przeczytany, bez plakietki);
 * filtr „Istotne” (`importance: 'important'`) go chowa. Zdarzenia FYI innych modułów zostają historią.
 */
export const INBOX_HIDDEN_SEVERITIES = ['fyi', 'approval'] as const satisfies readonly Severity[];

/** Typy zdarzeń widoczne w Inboxie także jako „Do wiadomości” („Mniej ważne”): każda odebrana poczta. */
export const INBOX_VISIBLE_FYI_TYPES = ['message.received'] as const;

/** Czy zdarzenie jest na liście Inboxu bez jawnego filtra ważności (to samo co `severityWhere` w @fos/core). */
export function isInboxListed(e: { type: string; severity: Severity }, importance: InboxImportance = 'all'): boolean {
  if (!(INBOX_HIDDEN_SEVERITIES as readonly string[]).includes(e.severity)) return true;
  return (
    importance === 'all' && e.severity === 'fyi' && (INBOX_VISIBLE_FYI_TYPES as readonly string[]).includes(e.type)
  );
}

/** Filtr „Istotne / Wszystkie”: `important` chowa mniej ważną pocztę (FYI), `all` pokazuje każdą odebraną. */
export const INBOX_IMPORTANCE_FILTERS = ['all', 'important'] as const;
export type InboxImportance = (typeof INBOX_IMPORTANCE_FILTERS)[number];

/** Ważności, które właściciel może nadać ręcznie (`approval` wynika ze stanu akceptacji, nie z klasyfikacji). */
export const RECLASSIFIABLE_SEVERITIES = ['critical', 'normal', 'fyi'] as const satisfies readonly Severity[];
export type ReclassifiableSeverity = (typeof RECLASSIFIABLE_SEVERITIES)[number];

/** Rodzaj pozycji listy: wiadomość (wątek poczty, Fala 4) albo zdarzenie systemu. */
export const INBOX_ITEM_KINDS = ['message', 'event'] as const;
export type InboxItemKind = (typeof INBOX_ITEM_KINDS)[number];

/** `message.*` = wiadomość (nadawca, temat, treść, wątek `message_thread` w `links`); reszta = zdarzenie. */
export function inboxItemKindOf(type: string): InboxItemKind {
  return type.startsWith('message.') ? 'message' : 'event';
}

/**
 * Ścieżka SPA dla odnośnika zdarzenia (`events.links`). `null` = typ bez własnej strony (np. pojedyncza wiadomość —
 * prowadzi do niej wątek: `messagePath` w resolverze powiązań, monitor — ścieżkę do zasobu daje resolver powiązań). Akceptacja zawsze prowadzi do modułu
 * Akceptacje (link „Przejdź do akceptacji”).
 */
export function inboxLinkPath(link: { type: string; id: string }): string | null {
  switch (link.type) {
    case 'approval':
      return `/akceptacje?id=${link.id}`;
    case 'client':
      return `/klienci/${link.id}`;
    case 'project':
      return `/projekty/${link.id}`;
    case 'task':
      return `/zadania/${link.id}`;
    case 'hosted_asset':
      return `/utrzymanie/${link.id}`;
    case 'billing_draft':
      return `/rozliczenia/${link.id}`;
    case 'meeting':
      return `/spotkania/${link.id}`;
    case 'offer':
      return `/oferty/${link.id}`;
    case 'knowledge_item':
      return `/wiedza/${link.id}`;
    case 'message_thread':
      return `/poczta/${link.id}`;
    case 'incident':
      return `/incydenty/${link.id}`;
    case 'integration':
      return INTEGRATIONS_SETTINGS_PATH;
    default:
      return null;
  }
}

/** Ustawienia → Integracje (odnośnik `integration`: utrata autoryzacji skrzynki, błąd połączenia). */
export const INTEGRATIONS_SETTINGS_PATH = '/ustawienia/integracje';

/** Strona wiadomości = jej wątek (odnośnik `message` nie ma własnej strony). */
export function messagePath(threadId: string, _messageId?: string): string {
  return `/poczta/${threadId}`;
}

/** Najdłuższe odłożenie (rok). */
export const INBOX_MAX_SNOOZE_DAYS = 365;

// ── Wejścia usług (router `inbox`) ──────────────────────────────────────────

export const INBOX_SCOPES = ['mine', 'team'] as const;
export type InboxScope = (typeof INBOX_SCOPES)[number];

/**
 * Filtr listy (zakładki): `unread` Nieprzeczytane, `open` Wszystkie (nieprzeczytane + przeczytane, bez odłożonych
 * i zrobionych; domyślny), `snoozed` Odłożone, `done` Zrobione, `all` wszystko (także odłożone i zrobione).
 */
export const INBOX_FILTERS = ['unread', 'open', 'snoozed', 'done', 'all'] as const;
export type InboxFilter = (typeof INBOX_FILTERS)[number];

/**
 * Lista: `mine` = moje pozycje; `team` = pozycje wszystkich osób (owner/admin/manager; `InboxItemDto.userId` = odbiorca,
 * akcje działają tylko na własnych). Pozycja jest widoczna, dopóki osoba ma podgląd modułu zdarzenia i dostęp do jego
 * klienta albo projektu (odebranie dostępu ukrywa pozycje od razu).
 */
export const listInboxInput = z
  .object({
    scope: z.enum(INBOX_SCOPES).default('mine'),
    filter: z.enum(INBOX_FILTERS).default('open'),
    severity: severitySchema.optional(),
    /** „Istotne” (bez mniej ważnej poczty) / „Wszystkie” (domyślnie: każda odebrana poczta) */
    importance: z.enum(INBOX_IMPORTANCE_FILTERS).default('all'),
    clientId: idSchema.optional(),
    source: eventSourceSchema.optional(),
    limit: z.number().int().min(1).max(200).default(50),
    /** kursor z poprzedniej strony (`InboxListPage.nextCursor`) */
    cursor: z.string().max(120).optional(),
  })
  .default({});
export type ListInboxInput = z.input<typeof listInboxInput>;

export const inboxItemRefInput = z.object({ id: idSchema });

/** Moja pozycja Inboxu dla zdarzenia (adres z powiadomienia push: `/inbox?zdarzenie=<eventId>`). */
export const inboxItemForEventInput = z.object({ eventId: idSchema });

/** Kilka pozycji naraz („Przeczytane”, „Nieprzeczytane”, „Zrobione”, „Przywróć”). */
export const inboxIdsInput = z.object({
  ids: z.array(idSchema).min(1).max(200),
});
export type InboxIdsInput = z.input<typeof inboxIdsInput>;

/** „Oznacz wszystkie jako przeczytane” (opcjonalnie tylko w bieżącym filtrze). */
export const markAllReadInput = z
  .object({
    severity: severitySchema.optional(),
    importance: z.enum(INBOX_IMPORTANCE_FILTERS).optional(),
    clientId: idSchema.optional(),
    source: eventSourceSchema.optional(),
  })
  .default({});

/** „Odłóż do…” (null = przywróć teraz). */
export const snoozeInboxInput = z.object({
  id: idSchema,
  until: z.coerce.date().nullable(),
});

/**
 * Poprawa klasyfikacji (MVP §9: „właściciel może poprawić klasyfikację”; owner/admin/manager). `severity: null`
 * (albo ważność źródła) = przywróć klasyfikację źródła. Zmiana wymaga powodu (trafia do Audit Log i podglądu).
 */
export const reclassifyEventInput = z
  .object({
    eventId: idSchema,
    severity: z
      .enum(RECLASSIFIABLE_SEVERITIES, { errorMap: () => ({ message: 'Wybierz: Pilne, Zwykłe albo Do wiadomości' }) })
      .nullable(),
    reason: z.string().trim().max(500).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.severity !== null && !v.reason) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['reason'], message: 'Napisz krótko, dlaczego zmieniasz' });
    }
  });
export type ReclassifyEventInput = z.input<typeof reclassifyEventInput>;

/** Zdarzenia klienta do zakładki Historia / Wiadomości (wszystkie ważności, także FYI). */
export const clientTimelineInput = z.object({
  clientId: idSchema,
  limit: z.number().int().min(1).max(100).default(30),
  cursor: z.string().max(120).optional(),
});
export type ClientTimelineInput = z.input<typeof clientTimelineInput>;

// ── Symulacja (Etap 4: „Użyj symulowanych zdarzeń”; tylko NODE_ENV=development, owner/admin) ─────────────

export const SIMULATED_EVENT_KINDS = ['message', 'outage', 'agent_finished'] as const;
export type SimulatedEventKind = (typeof SIMULATED_EVENT_KINDS)[number];

/** Typ zdarzenia dla rodzaju symulacji (zachowuje się jak prawdziwe zdarzenie tego typu). */
export const SIMULATED_EVENT_TYPES: Readonly<Record<SimulatedEventKind, string>> = {
  message: 'message.received',
  outage: 'monitor.down',
  agent_finished: 'agent_run.finished',
};

export const SIMULATION_AUDIENCES = ['me', 'record'] as const;
export type SimulationAudience = (typeof SIMULATION_AUDIENCES)[number];

export const EVENT_SIMULATION_NOTE = 'Symulacja — zdarzenie testowe, nic nie zostało wysłane';

export const simulateEventInput = z
  .object({
    kind: z.enum(SIMULATED_EVENT_KINDS),
    severity: z.enum(RECLASSIFIABLE_SEVERITIES).default('normal'),
    clientId: idSchema.nullable().optional(),
    title: z.string().trim().min(1, 'Wpisz temat').max(300),
    body: z.string().trim().max(20_000).optional(),
    sender: eventSenderSchema.optional(),
    /** `me` = tylko ja (domyślnie, żeby nie zaśmiecać Inboxów zespołu), `record` = wszyscy z dostępem do klienta */
    audience: z.enum(SIMULATION_AUDIENCES).default('me'),
  })
  .superRefine((v, ctx) => {
    if (v.kind === 'message' && !v.clientId && !v.sender) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['clientId'], message: 'Wybierz klienta albo wpisz nadawcę' });
    }
  });
export type SimulateEventInput = z.input<typeof simulateEventInput>;

// ── Wyjścia (DTO) ───────────────────────────────────────────────────────────

export interface EventDto {
  id: string;
  type: string;
  /** wiadomość (wątek poczty) albo zdarzenie systemu */
  kind: InboxItemKind;
  /** obowiązująca ważność (po poprawce) */
  severity: Severity;
  /** ważność nadana przez źródło */
  originalSeverity: Severity;
  override: { by: { userId: string; name: string } | null; at: Date } | null;
  title: string;
  body: string | null;
  source: EventSource;
  /** zdarzenie z narzędzia „Symuluj zdarzenie” (`source = 'simulation'`): w UI zawsze oznaczone „Symulacja” */
  simulated: boolean;
  sender: string | null;
  client: { id: string; name: string; color: string | null } | null;
  projectId: string | null;
  module: ModuleKey | null;
  links: { type: string; id: string }[];
  /** wątek poczty (`links` typu `message_thread`, Fala 4) */
  threadId: string | null;
  reason: string | null;
  occurredAt: Date;
}

export interface InboxItemDto {
  id: string;
  status: InboxStatus;
  group: InboxGroup;
  snoozedUntil: Date | null;
  readAt: Date | null;
  doneAt: Date | null;
  /** odbiorca (w trybie „Zespół” różny od osoby) */
  userId: string;
  event: EventDto;
  /** akceptacja powiązana ze zdarzeniem (link „Przejdź do akceptacji”) */
  approvalId: string | null;
}

export interface InboxListPage {
  items: InboxItemDto[];
  /** kursor następnej strony (`null` = koniec) */
  nextCursor: string | null;
}

/** Powiązanie zdarzenia w podglądzie (tylko rekordy, które osoba widzi). */
export interface InboxLinkDto {
  type: string;
  id: string;
  /** nazwa rekordu („REA Nieruchomości”, „#12 Formularz kontaktowy”, „Akceptacja · Czeka”) */
  label: string;
  path: string | null;
}

export interface InboxItemDetail extends InboxItemDto {
  links: InboxLinkDto[];
  /** główne przejście: akceptacja („Przejdź do akceptacji”) albo pierwszy widoczny rekord */
  goTo: { label: string; path: string } | null;
  /** powód poprawki klasyfikacji (z Audit Log) */
  overrideReason: string | null;
  /** owner/admin/manager z edycją Inboxu, zdarzenie nie jest akceptacją */
  canReclassify: boolean;
}

export interface InboxCounts {
  /** nieprzeczytane, nieodłożone, ważność z `INBOX_BADGE_SEVERITIES` (plakietka „Inbox”) */
  unread: number;
  /** nieprzeczytane `critical` (kropka „Pilne”) */
  urgent: number;
  /** odłożone (link „Odłożone” pod listą) */
  snoozed: number;
}

export interface ClientTimelineItem {
  eventId: string;
  type: string;
  kind: InboxItemKind;
  severity: Severity;
  title: string;
  /** początek treści (do 240 znaków) */
  excerpt: string | null;
  sender: string | null;
  source: EventSource;
  simulated: boolean;
  occurredAt: Date;
  /** moja pozycja Inboxu (link do podglądu), jeśli zdarzenie do mnie trafiło */
  inboxItemId: string | null;
}

export interface ClientTimelinePage {
  items: ClientTimelineItem[];
  nextCursor: string | null;
}

export interface SimulateEventResult {
  eventId: string;
  /** moja pozycja Inboxu (gdy symulacja trafiła do mnie) */
  inboxItemId: string | null;
  recipients: number;
}

export const INBOX_MESSAGES = {
  notFound: 'Nie znaleziono wiadomości',
  onlyOwnerReclassifies: 'Ważność zmienia właściciel, administrator albo manager',
  approvalNotReclassifiable: 'Ważność akceptacji wynika z jej stanu — decyzję podejmij w Akceptacjach',
  snoozeInPast: 'Wybierz termin w przyszłości',
  snoozeTooFar: 'Odłożyć można najwyżej o rok',
  snoozeDone: 'Ta wiadomość jest już zrobiona',
  teamScopeForbidden: 'Pozycje zespołu widzi właściciel, administrator albo manager',
  badCursor: 'Nieprawidłowy kursor listy',
  simulationDevOnly: 'Symulacja zdarzeń działa tylko w środowisku deweloperskim',
  simulationAdminOnly: 'Zdarzenia symuluje właściciel albo administrator',
} as const;
